import axios from 'axios';
import { getApiBaseUrl } from '../config/env';

// ---------------------------------------------------------------------------
// authStore
// ---------------------------------------------------------------------------
/**
 * Persistent authentication store.
 *
 * Holds the access/refresh JWTs returned by the backend auth endpoints plus
 * the full UserResponse object. Tokens are mirrored to localStorage so the
 * session survives full page reloads. Call `authStore.hydrate()` once on app
 * boot to rebuild in-memory state from storage.
 *
 * Shape:
 *   accessToken  - short-lived JWT used as the Bearer token on every request
 *   refreshToken - long-lived JWT used to obtain new access tokens
 *   user         - full UserResponse: { id, email, fullName, avatarUrl, role, emailVerified }
 */
const STORAGE_KEY = 'samastix:auth';

function readStorage() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeStorage(payload) {
  try {
    if (payload) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* storage unavailable - ignore */
  }
}

export const authStore = {
  accessToken:  null,
  refreshToken: null,
  user:         null,

  set(authResponse) {
    this.accessToken  = authResponse.accessToken;
    this.refreshToken = authResponse.refreshToken;
    this.user         = authResponse.user;
    writeStorage({
      accessToken:  this.accessToken,
      refreshToken: this.refreshToken,
      user:         this.user,
    });
    console.log('[authStore] credentials set - user:', this.user?.email, 'role:', this.user?.role);
  },

  clear() {
    this.accessToken  = null;
    this.refreshToken = null;
    this.user         = null;
    writeStorage(null);
    console.log('[authStore] credentials cleared');
  },

  /** Restore tokens from localStorage. Returns true if anything was loaded. */
  hydrate() {
    const stored = readStorage();
    if (!stored) return false;
    this.accessToken  = stored.accessToken  || null;
    this.refreshToken = stored.refreshToken || null;
    this.user         = stored.user         || null;
    console.log('[authStore] hydrated from storage - user:', this.user?.email);
    return !!(this.accessToken || this.refreshToken);
  },

  get isAuthenticated() {
    return !!this.accessToken;
  },

  get role() {
    return this.user?.role;
  },
};

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

const BASE_URL = getApiBaseUrl();

const client = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// Request interceptor - attach Bearer token when available
client.interceptors.request.use(
  (config) => {
    config.baseURL = getApiBaseUrl() || 'http://localhost:8080';
    const { accessToken } = authStore;
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    console.log(`[API] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    return config;
  },
  (error) => {
    console.error('[API] Request error:', error);
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// Silent refresh
// ---------------------------------------------------------------------------

let refreshPromise = null;

function performRefresh() {
  if (refreshPromise) return refreshPromise;

  const refreshToken = authStore.refreshToken;
  if (!refreshToken) return Promise.reject(new Error('No refresh token available'));

  const baseUrl = getApiBaseUrl() || 'http://localhost:8080';
  console.log('[API] Attempting silent token refresh to', baseUrl);
  refreshPromise = axios
    .post(`${baseUrl}/api/auth/refresh`, { refreshToken }, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 30_000,
    })
    .then((res) => {
      console.log('[API] Silent refresh succeeded');
      authStore.set(res.data);
      return res.data;
    })
    .finally(() => { refreshPromise = null; });

  return refreshPromise;
}

function redirectToLogin() {
  const path = window.location.pathname;
  if (path === '/login' || path === '/register') return;
  console.warn('[API] Redirecting to /login');
  window.location.href = '/login';
}

// Response interceptor - handle 401 with silent refresh + retry
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status         = error.response?.status;
    const originalConfig = error.config;
    const requestUrl     = originalConfig?.url || '';
    const isAuthEndpoint = requestUrl.includes('/api/auth/');

    if (status === 401 && originalConfig && !originalConfig._retry && !isAuthEndpoint) {
      originalConfig._retry = true;
      try {
        await performRefresh();
        originalConfig.headers['Authorization'] = `Bearer ${authStore.accessToken}`;
        console.log('[API] Retrying original request after refresh:', requestUrl);
        return client(originalConfig);
      } catch {
        console.warn('[API] Silent refresh failed - clearing auth and redirecting to /login');
        authStore.clear();
        redirectToLogin();
      }
    } else if (status === 401 && !isAuthEndpoint) {
      authStore.clear();
      redirectToLogin();
    }

    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'An unexpected error occurred';

    const enrichedError = new Error(message);
    enrichedError.status  = status;
    enrichedError.data    = error.response?.data;
    enrichedError.isAxios = true;

    console.error('[API] Response error:', status, message);
    return Promise.reject(enrichedError);
  }
);

export default client;
